/**
 * MorphViewerPage — Replaces GestureViewer as the default shop viewer.
 *
 * Architecture: morph-viewer-v2.html runs in a fullscreen <iframe>.
 * React overlays sit on top and communicate via postMessage:
 *
 *   iframe → React:
 *     {type:'frame-change', frameIndex}   — frame navigated
 *     {type:'tap', imagePixelX, imagePixelY, frameIndex} — user tapped (disk handled by React)
 *     {type:'mark-item', item}            — user marked an item inside iframe
 *     {type:'ready'}                      — iframe finished loading
 *
 *   React → iframe:
 *     {type:'navigate-to-frame', frameIndex}    — orbit step
 *     {type:'section-info', name, index, total} — section switch
 *
 * Preserved features (unchanged React components):
 *   - FrameItemMarkers  (marked items with 3D projection)
 *   - TapCameraIndicator  (disk blocker + camera discovery + orbit)
 *   - SectionsDrawer  (section/room switching)
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { useAppStore } from '@store/appStore'
import { useAuthStore, getAuthHeaders } from '@/stores/authStore'
import { buildMorphUrl } from '@/utils/buildMorphUrl'
import { buildOrbitSequence } from '@/utils/orbitNavigation'
import { calculateCameraVisibility } from '@/utils/cameraProjection'
import { calculateIndicatorPosition } from './calculateIndicatorPosition'
import { useMarkItem } from '@/hooks/useMarkItem'
import { useStableOffsetMap } from '@/hooks/useConnectedFrames'
import { TapCameraIndicator } from './TapCameraIndicator'
import { FrameItemMarkers } from './FrameItemMarkers'
import { SectionsDrawer } from '@/components/SectionsDrawer/SectionsDrawer'
import { LoadingProgressBar } from '@/components/LoadingProgressBar/LoadingProgressBar'
import { apiUrl } from '@/config'
import { getGuestId } from '@/utils/guestIdentity'
import type { TapIndicatorState } from '@/types/TapIndicatorTypes'
import './GestureViewer.css'

interface MorphViewerPageProps {
  shopId: string
}

export function MorphViewerPage({ shopId }: MorphViewerPageProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [morphUrl, setMorphUrl] = useState<string | null>(null)
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0)
  const [tapIndicator, setTapIndicator] = useState<TapIndicatorState | null>(null)
  const [tapIndicatorPannedPosition, setTapIndicatorPannedPosition] = useState<{ x: number; y: number } | null>(null)
  const visitNotifiedRef = useRef(false)

  // Store selectors
  const cameras = useAppStore((s) => s.cameras)
  const sections = useAppStore((s) => s.sections)
  const canUseTapIndicator = useAppStore((s) => s.canUseTapIndicator)
  const isLoadingData = useAppStore((s) => s.isLoadingData)
  const loadingProgress = useAppStore((s) => s.loadingProgress)
  const loadingStage = useAppStore((s) => s.loadingStage)
  const currentDatasetId = useAppStore((s) => s.currentDatasetId)
  const loadDataset = useAppStore((s) => s.loadDataset)
  const availableDatasets = useAppStore((s) => s.availableDatasets)

  // 3D offset map for camera ranking
  const rawOffsetMap = useAppStore((s) => s.offsetMap)
  const offsetMap = useStableOffsetMap(rawOffsetMap)

  // Marked items hook
  const { handleMarkItem } = useMarkItem({ shopId })

  // Current frame camera
  const frameCamera = cameras[currentFrameIndex] ?? cameras[0]

  // Display dimensions (full viewport since iframe is fullscreen)
  const displayWidth = window.innerWidth
  const displayHeight = window.innerHeight

  // ── Step 1: Load dataset into Zustand store (same as GestureViewer) ──────────
  useEffect(() => {
    if (shopId && shopId !== currentDatasetId) {
      const urlParams = new URLSearchParams(window.location.search)
      const targetSection = urlParams.get('section') ? parseInt(urlParams.get('section')!, 10) : undefined
      const targetFrame = urlParams.get('frame') ? parseInt(urlParams.get('frame')!, 10) : undefined
      useAppStore.getState().setLoadingData(true)
      loadDataset(shopId, { section: targetSection, frame: targetFrame })
    }
  }, [shopId, currentDatasetId, loadDataset])

  // ── Step 2: Build morph iframe URL ───────────────────────────────────────────
  useEffect(() => {
    if (!shopId) return
    // Use dataset paths if available, fall back to default paths using shopId
    const dataset = availableDatasets.find((d) => d.id === shopId) ?? { id: shopId }
    console.log('[MorphViewerPage] Building morph URL for', shopId, dataset)
    buildMorphUrl(dataset)
      .then((url) => {
        console.log('[MorphViewerPage] morphUrl set:', url)
        setMorphUrl(url)
      })
      .catch((err) => {
        console.error('[MorphViewerPage] buildMorphUrl failed:', err)
        // Last-resort fallback
        setMorphUrl(`/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(`/api/scan-data/${shopId}/section_0/`)}`)
      })
  }, [shopId, availableDatasets])

  // ── Step 3: Track shop visit (same as GestureViewer) ─────────────────────────
  useEffect(() => {
    if (!shopId || !currentDatasetId || visitNotifiedRef.current) return
    visitNotifiedRef.current = true
    const authUser = useAuthStore.getState().user
    const visitorId = authUser?.id || getGuestId()
    const visitorName = authUser?.name || undefined
    fetch(apiUrl(`/api/shops/${encodeURIComponent(shopId)}/visit`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ visitorId, visitorName }),
    }).catch(() => {})
    import('../../services/activityService').then(({ trackVisit }) => { trackVisit(shopId) })
  }, [shopId, currentDatasetId])

  // ── Step 4: postMessage bridge ────────────────────────────────────────────────
  const sendToIframe = useCallback((msg: object) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(msg), '*')
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return
      let msg: any
      try { msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data } catch { return }

      switch (msg.type) {
        case 'ready':
          break

        case 'frame-change':
          if (typeof msg.frameIndex === 'number') {
            setCurrentFrameIndex(msg.frameIndex)
            setTapIndicator(null) // dismiss disk on manual navigation
          }
          break

        case 'tap': {
          if (!canUseTapIndicator || !cameras.length) break
          const { imagePixelX, imagePixelY, frameIndex: tapFrame = currentFrameIndex } = msg
          const cam = cameras[tapFrame] ?? cameras[0]
          if (!cam) break

          const imgW = cam.intrinsics.width
          const imgH = cam.intrinsics.height

          // Use image dimensions as the container rect (same pattern as NewModeViewer shared URL tap)
          const fakeRect = new DOMRect(0, 0, imgW, imgH)
          const points3D = useAppStore.getState().points3D
          const { highlightedCameras, projections, point3D } = calculateCameraVisibility(
            imagePixelX, imagePixelY, fakeRect, cam, cameras, points3D, offsetMap, tapFrame
          )

          // Scale image-space coords to display coords
          const displayX = (imagePixelX / imgW) * displayWidth
          const displayY = (imagePixelY / imgH) * displayHeight

          // Build orbit sequence
          if (point3D) {
            const orbit = buildOrbitSequence(point3D, projections, cameras, tapFrame)
            const store = useAppStore.getState()
            store.setOrbitSequence?.(orbit)
            store.setOrbitIndex?.(0)
          }

          setTapIndicatorPannedPosition(null)
          setTapIndicator({
            show: true,
            cameraCount: highlightedCameras.size,
            screenPosition: { x: displayX, y: displayY },
            frameRelativeTapX: imagePixelX,
            frameRelativeTapY: imagePixelY,
            imagePixelX,
            imagePixelY,
            currentDisplayX: displayX,
            currentDisplayY: displayY,
            projections,
            point3D,
            frameCamera: cam,
            frameIndex: tapFrame,
            tappedFrameIndex: tapFrame,
            is2DOnly: !point3D,
          })
          break
        }

        case 'mark-item': {
          // Morph viewer marked an item natively — save to CartStore
          if (msg.item) {
            const { imagePixelX, imagePixelY, frameIndex, point3D } = msg.item
            handleMarkItem({
              imagePixelX: imagePixelX ?? 0,
              imagePixelY: imagePixelY ?? 0,
              radius: 30,
              point3D: point3D ?? null,
              projections: new Map(),
              frameIndex: frameIndex ?? currentFrameIndex,
            })
          }
          break
        }
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [cameras, canUseTapIndicator, currentFrameIndex, displayWidth, displayHeight, offsetMap, handleMarkItem])

  // ── Orbit helpers ─────────────────────────────────────────────────────────────
  const handleOrbitStep = useCallback((targetFrameIndex: number) => {
    sendToIframe({ type: 'navigate-to-frame', frameIndex: targetFrameIndex })
    setCurrentFrameIndex(targetFrameIndex)
    // Update tap indicator position for new frame
    if (tapIndicator) {
      const cam = cameras[targetFrameIndex]
      if (!cam) return
      const proj = tapIndicator.projections.get(targetFrameIndex)
      if (!proj) return
      const [dx, dy] = calculateIndicatorPosition(proj, cam.intrinsics.width, cam.intrinsics.height, displayWidth, displayHeight)
      setTapIndicator((prev) => prev ? { ...prev, frameIndex: targetFrameIndex, frameCamera: cam, currentDisplayX: dx, currentDisplayY: dy } : null)
      setTapIndicatorPannedPosition(null)
    }
  }, [sendToIframe, tapIndicator, cameras, displayWidth, displayHeight])

  const handleOrbitPrev = useCallback(() => {
    const store = useAppStore.getState()
    const seq = store.orbitSequence
    const idx = store.orbitIndex
    if (!seq || seq.length < 2) return
    const prevIdx = (idx - 1 + seq.length) % seq.length
    store.setOrbitIndex(prevIdx)
    handleOrbitStep(seq[prevIdx].frameIndex)
  }, [handleOrbitStep])

  const handleOrbitNext = useCallback(() => {
    const store = useAppStore.getState()
    const seq = store.orbitSequence
    const idx = store.orbitIndex
    if (!seq || seq.length < 2) return
    const nextIdx = (idx + 1) % seq.length
    store.setOrbitIndex(nextIdx)
    handleOrbitStep(seq[nextIdx].frameIndex)
  }, [handleOrbitStep])

  // ── TapCameraIndicator derived values ─────────────────────────────────────────
  const indicatorDisplayX = tapIndicator ? calculateIndicatorPosition(
    [tapIndicator.imagePixelX, tapIndicator.imagePixelY],
    frameCamera?.intrinsics?.width ?? 450,
    frameCamera?.intrinsics?.height ?? 800,
    displayWidth,
    displayHeight,
  )[0] : 0

  const indicatorDisplayY = tapIndicator ? calculateIndicatorPosition(
    [tapIndicator.imagePixelX, tapIndicator.imagePixelY],
    frameCamera?.intrinsics?.width ?? 450,
    frameCamera?.intrinsics?.height ?? 800,
    displayWidth,
    displayHeight,
  )[1] : 0

  const indicatorPosition = tapIndicatorPannedPosition ?? { x: indicatorDisplayX, y: indicatorDisplayY }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="gesture-viewer" style={{ position: 'fixed', inset: 0, background: '#000' }}>
      {/* Loading state while morph URL is being fetched */}
      {!morphUrl && (
        <div className="gesture-viewer__loading">
          <button
            className="gesture-viewer__cancel-btn"
            onClick={() => window.location.href = '/'}
            title="Cancel"
          >✕</button>
          <div className="spinner" />
          <p>Loading shop...</p>
        </div>
      )}

      {/* Section-switch progress bar */}
      {isLoadingData && canUseTapIndicator && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 500, pointerEvents: 'none' }}>
          <LoadingProgressBar progress={loadingProgress} stage={loadingStage || 'Loading section...'} />
        </div>
      )}

      {/* ── The morph viewer iframe ── */}
      {morphUrl && (
        <iframe
          ref={iframeRef}
          src={morphUrl}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          allow="fullscreen"
          title="Shop Viewer"
        />
      )}

      {/* ── React overlays on top of iframe ── */}

      {/* Marked item circles + flags */}
      {frameCamera && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          <FrameItemMarkers
            frameIndex={currentFrameIndex}
            frameCamera={frameCamera}
            frameDisplayWidth={displayWidth}
            frameDisplayHeight={displayHeight}
            shopId={shopId}
          />
        </div>
      )}

      {/* TapCameraIndicator — disk blocker + orbit + discovery */}
      {canUseTapIndicator && tapIndicator && tapIndicator.show && frameCamera && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, pointerEvents: 'none' }}>
          <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'none' }}>
            <TapCameraIndicator
              cameraCount={tapIndicator.cameraCount}
              position={indicatorPosition}
              currentDisplayX={indicatorDisplayX}
              currentDisplayY={indicatorDisplayY}
              frameRelativeTapX={tapIndicator.frameRelativeTapX}
              frameRelativeTapY={tapIndicator.frameRelativeTapY}
              imagePixelX={tapIndicator.imagePixelX}
              imagePixelY={tapIndicator.imagePixelY}
              frameCamera={frameCamera}
              frameIndex={currentFrameIndex}
              cameras={cameras}
              points3D={useAppStore.getState().points3D}
              offsetMap={offsetMap}
              frameDisplayWidth={displayWidth}
              radiusPxImageSpace={tapIndicator.is2DOnly ? undefined : tapIndicator.radiusPxImageSpace}
              scaleData={tapIndicator.is2DOnly ? undefined : tapIndicator.scaleData}
              zoomScale={1}
              onShowThumbnails={() => {}}
              onPanComplete={(_finalScreenX, _finalScreenY, finalDisplayX, finalDisplayY) => {
                setTapIndicatorPannedPosition({ x: finalDisplayX, y: finalDisplayY })
                // Convert display position back to image pixel for new discovery origin
                const cam = frameCamera
                const newImagePixelX = (finalDisplayX / displayWidth) * (cam?.intrinsics?.width ?? 450)
                const newImagePixelY = (finalDisplayY / displayHeight) * (cam?.intrinsics?.height ?? 800)
                setTapIndicator((prev) => prev ? { ...prev, imagePixelX: newImagePixelX, imagePixelY: newImagePixelY } : null)
              }}
              onClose={() => setTapIndicator(null)}
              shopId={shopId}
              is2DOnly={tapIndicator.is2DOnly}
              onMarkItem={() => {
                handleMarkItem({
                  imagePixelX: tapIndicator.imagePixelX,
                  imagePixelY: tapIndicator.imagePixelY,
                  radius: tapIndicator.is2DOnly ? 30 : (tapIndicator.radiusPxImageSpace ?? 30),
                  point3D: tapIndicator.point3D,
                  projections: tapIndicator.projections,
                  frameIndex: tapIndicator.frameIndex,
                })
                setTapIndicator(null)
              }}
              onOrbitPrev={handleOrbitPrev}
              onOrbitNext={handleOrbitNext}
            />
          </div>
        </div>
      )}

      {/* SectionsDrawer — section/room switching */}
      {canUseTapIndicator && sections.length > 0 && (
        <SectionsDrawer />
      )}
    </div>
  )
}

export default MorphViewerPage
